import { all, call, put, takeEvery } from 'redux-saga/effects'
import { t } from 'ttag'
import { addNotification } from '../../../common/notifications/NotificationActions'
import { INotification } from '../../../common/notifications/NotificationReducers'
import api from '../../../services/api.services'
import { getErrorMsg } from '../../../common/helpers/translations'
import ApiClient from '../../../services/ApiClient'
import {
  getCompanyDomains,
  getCompanyDomainsSuccess,
  getCompanyDomainsFail,
  addCompanyDomain,
  addCompanyDomainSuccess,
  addCompanyDomainFail,
  removeCompanyDomain,
  removeCompanyDomainSuccess,
  removeCompanyDomainFail,
  verifyCompanyDomain,
  verifyCompanyDomainSuccess,
  verifyCompanyDomainFail,
  getCompanyDomainSettings,
  getCompanyDomainSettingsSuccess,
  getCompanyDomainSettingsFail,
  updateCompanyDomainSettings,
  updateCompanyDomainSettingsSuccess,
  updateCompanyDomainSettingsFail,
} from './DomainAdminActions'

/**
 * Saga: fetch all domains for a company.
 */
function* sagaGetCompanyDomains({ payload }: any) {
  try {
    const { companyId } = payload
    const response: any = yield call(api.auth.getCompanyDomains, { companyId })
    yield put({ type: String(getCompanyDomainsSuccess), payload: response })
  } catch (e: any) {
    yield put({ type: String(getCompanyDomainsFail) })
    const notification: INotification = {
      type: 'warning',
      content: getErrorMsg('getCompanyDomains'),
    }
    yield put({ type: String(addNotification), payload: notification })
  }
}

/**
 * Saga: add a new domain to a company.
 * Reloads the domain list on success.
 */
function* sagaAddCompanyDomain({ payload }: any) {
  try {
    const response: any = yield call(api.auth.addCompanyDomain, payload)
    const addedDomain = response?.domain
    yield put({ type: String(addCompanyDomainSuccess), payload: addedDomain })

    // Reload domains to get the latest list
    yield put({ type: String(getCompanyDomains), payload: { companyId: payload.companyId } })
  } catch (e: any) {
    yield put({ type: String(addCompanyDomainFail) })

    let errorContent: any
    if (e.message === 'domainAlreadyClaimed') {
      errorContent = t`This domain has already been claimed.`
    } else if (e.message === 'invalidDomain') {
      errorContent = t`Invalid domain format. Please enter a valid domain (e.g. example.com).`
    } else {
      errorContent = getErrorMsg('addCompanyDomain')
    }

    const notification: INotification = {
      type: 'warning',
      content: errorContent,
    }
    yield put({ type: String(addNotification), payload: notification })
  }
}

/**
 * Saga: remove a domain from a company.
 * Reloads the domain list on success.
 */
function* sagaRemoveCompanyDomain({ payload }: any) {
  try {
    yield call(api.auth.removeCompanyDomain, payload)
    yield put({ type: String(removeCompanyDomainSuccess) })

    const notification: INotification = {
      type: 'success',
      content: t`Domain removed.`,
    }
    yield put({ type: String(addNotification), payload: notification })

    // Reload domains
    yield put({ type: String(getCompanyDomains), payload: { companyId: payload.companyId } })
  } catch (e: any) {
    yield put({ type: String(removeCompanyDomainFail) })
    const notification: INotification = {
      type: 'warning',
      content:
        e.message && e.message !== 'No message has been provided by the server'
          ? e.message
          : getErrorMsg('removeCompanyDomain'),
    }
    yield put({ type: String(addNotification), payload: notification })
  }
}

/**
 * Saga: trigger verification for a domain.
 * Reloads the domain list on success to reflect updated status.
 */
function* sagaVerifyCompanyDomain({ payload }: any) {
  try {
    const response: any = yield call(api.auth.verifyCompanyDomain, payload)
    yield put({ type: String(verifyCompanyDomainSuccess) })

    const status = response?.domain?.status?.toLowerCase()
    const notification: INotification =
      status === 'verified'
        ? { type: 'success', content: t`Domain verified successfully.` }
        : {
            type: 'warning',
            content: t`DNS record not found. Make sure you have added the correct TXT record and try again. DNS changes may take up to 48 hours to propagate.`,
          }
    yield put({ type: String(addNotification), payload: notification })

    // Reload domains to get the updated status
    yield put({ type: String(getCompanyDomains), payload: { companyId: payload.companyId } })
  } catch (e: any) {
    yield put({ type: String(verifyCompanyDomainFail) })
    const notification: INotification = {
      type: 'warning',
      content:
        e.message && e.message !== 'No message has been provided by the server'
          ? e.message
          : getErrorMsg('verifyCompanyDomain'),
    }
    yield put({ type: String(addNotification), payload: notification })
  }
}

/**
 * Saga: fetch domain settings for a company.
 */
function* sagaGetCompanyDomainSettings({ payload }: any) {
  try {
    const { companyId } = payload
    const response: any = yield call(ApiClient.post as any, 'getCompanyDomainSettings', { companyId })
    yield put({ type: String(getCompanyDomainSettingsSuccess), payload: response })
  } catch (e: any) {
    yield put({ type: String(getCompanyDomainSettingsFail) })
  }
}

/**
 * Saga: update domain settings for a company.
 */
function* sagaUpdateCompanyDomainSettings({ payload }: any) {
  try {
    const response: any = yield call(ApiClient.post as any, 'updateCompanyDomainSettings', payload)
    yield put({ type: String(updateCompanyDomainSettingsSuccess), payload: response })
    const notification: INotification = {
      type: 'success',
      content: t`Settings updated.`,
    }
    yield put({ type: String(addNotification), payload: notification })
  } catch (e: any) {
    yield put({ type: String(updateCompanyDomainSettingsFail) })
    const notification: INotification = {
      type: 'warning',
      content: getErrorMsg('updateCompanyDomainSettings'),
    }
    yield put({ type: String(addNotification), payload: notification })
  }
}

export function* domainAdminSagas() {
  yield all([
    takeEvery(String(getCompanyDomains), sagaGetCompanyDomains),
    takeEvery(String(addCompanyDomain), sagaAddCompanyDomain),
    takeEvery(String(removeCompanyDomain), sagaRemoveCompanyDomain),
    takeEvery(String(verifyCompanyDomain), sagaVerifyCompanyDomain),
    takeEvery(String(getCompanyDomainSettings), sagaGetCompanyDomainSettings),
    takeEvery(String(updateCompanyDomainSettings), sagaUpdateCompanyDomainSettings),
  ])
}
